// src/pages/property-types/PropertyTypesPage.tsx
import {useState} from "react";
import {Plus, Building, Edit, Trash2, Search} from "lucide-react";
import {PageHeader, DataTable} from "../../components/common";
import {Button, Input, Badge} from "../../components/ui";
import {usePropertyTypes} from "../../hooks/usePropertyTypes";
import {useTable} from "../../hooks/common/useTable";
import {useToast} from "../../hooks/common/useToast";
import {useConfirm} from "../../hooks/common/useConfirm";
import {useNavigate} from "react-router-dom";
import {ROUTES} from "../../utils/constants";
import {
  createCommonActions,
  createCommonBulkActions,
} from "../../components/common/DataTable/TableActions";
import type {
  PropertyTypeResponseDto,
  CreatePropertyTypeDto,
  UpdatePropertyTypeDto,
} from "../../types/property-type";
import type {
  DataTableColumn,
  DataTableAction,
} from "../../components/common/DataTable/DataTableTypes";

const PropertyTypesPage: React.FC = () => {
  const {success, error} = useToast();
  const {confirm} = useConfirm();
  const navigate = useNavigate();

  // Table state
  const tableKey = "property-types";
  const {
    currentPage,
    pageSize,
    getQueryParams,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    handleFilterChange,
    clearAllFilters,
    isFiltered,
  } = useTable({key: tableKey});

  // Table state only - create/edit handled by dedicated pages

  // Get property types data
  const {
    propertyTypes,
    pagination,
    stats,
    isLoading,
    handleCreate,
    handleUpdate,
    handleDelete,
  } = usePropertyTypes(getQueryParams());

  // Handle create - navigate to create page
  const handleCreatePropertyType = () => {
    navigate(ROUTES.PROPERTY_TYPE_CREATE);
  };

  // Handle edit - navigate to edit page
  const handleEditPropertyType = (propertyType: PropertyTypeResponseDto) => {
    navigate(`${ROUTES.PROPERTY_TYPES}/${propertyType.id}/edit`);
  };

  // Handle delete
  const handleDeletePropertyType = async (
    propertyType: PropertyTypeResponseDto
  ) => {
    const success = await handleDelete(propertyType.id);
    // handleDelete already shows confirmation dialog
  };

  // Table columns
  const columns: DataTableColumn<
    PropertyTypeResponseDto & Record<string, any>
  >[] = [
    {
      key: "icon",
      title: "Icon",
      render: (_, record) => (
        <div className="flex items-center justify-center w-10 h-10 bg-gray-100 dark:bg-dark-700 rounded-md overflow-hidden">
          {record.icon ? (
            <img
              src={record.icon}
              alt={`${record.nameEng} icon`}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to default icon if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.parentElement!.innerHTML = '<span class="text-lg">🏠</span>';
              }}
            />
          ) : (
            <span className="text-lg">🏠</span>
          )}
        </div>
      ),
      width: 80,
      align: "center",
    },
    {
      key: "nameEng",
      title: "Name (English)",
      sortable: true,
      render: (_, record) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">
            {record.nameEng}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {record.nameMm}
          </div>
        </div>
      ),
    },
    {
      key: "slug",
      title: "Slug",
      render: (_, record) => (
        <code className="px-2 py-1 text-xs bg-gray-100 dark:bg-dark-700 rounded">
          {record.slug}
        </code>
      ),
    },
    {
      key: "propertyCount",
      title: "Properties",
      align: "center",
      sortable: true,
      render: (_, record) => (
        <Badge variant={record.propertyCount > 0 ? "info" : "gray"}>
          {record.propertyCount}{" "}
          {record.propertyCount === 1 ? "property" : "properties"}
        </Badge>
      ),
    },
    {
      key: "createdAt",
      title: "Created",
      sortable: true,
      render: (_, record) => new Date(record.createdAt).toLocaleDateString(),
    },
  ];

  // Table actions
  const actions: DataTableAction<PropertyTypeResponseDto>[] = [
    createCommonActions<PropertyTypeResponseDto>().edit(handleEditPropertyType),
    createCommonActions<PropertyTypeResponseDto>().delete(
      handleDeletePropertyType
    ),
  ];

  // Bulk actions
  const bulkActions = [
    createCommonBulkActions<PropertyTypeResponseDto>().bulkDelete(
      async (records) => {
        const confirmed = await confirm({
          title: "Delete Property Types",
          message: `Are you sure you want to delete ${records.length} property types? This action cannot be undone.`,
          variant: "danger",
          confirmText: "Delete",
        });

        if (confirmed) {
          // TODO: Implement bulk delete API call
          success(
            "Success",
            `${records.length} property types deleted successfully`
          );
        }
      }
    ),
  ];

  // Form fields for create/edit
  const formFields = [
    {
      name: "nameEng",
      label: "Name (English)",
      type: "text" as const,
      required: true,
      placeholder: "Enter property type name in English",
      validation: {required: true, minLength: 1, maxLength: 100},
    },
    {
      name: "nameMm",
      label: "Name (Myanmar)",
      type: "text" as const,
      required: true,
      placeholder: "Enter property type name in Myanmar",
      validation: {required: true, minLength: 1, maxLength: 100},
    },
    // Note: Icon field will be handled by PropertyTypeIconUpload component in create/edit pages
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Property Types"
        breadcrumbs={[
          {label: "Dashboard", path: "/dashboard"},
          {label: "Property Types"},
        ]}
        action={{
          label: "Add Property Type",
          onClick: handleCreatePropertyType,
          icon: <Plus className="w-4 h-4 mr-2" />,
        }}
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-dark-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-dark-700">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/20">
                <Building className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Types
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.totalTypes}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-dark-700">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/20">
                <Building className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Most Used
                </p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {stats.mostUsedType}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {stats.mostUsedTypeCount} properties
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-dark-700">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100 dark:bg-yellow-900/20">
                <Building className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Least Used
                </p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {stats.leastUsedType}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {stats.leastUsedTypeCount} properties
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-dark-700">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/20">
                <Building className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Average Usage
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {(stats?.averagePropertiesPerType || 0).toFixed(1)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  per type
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-dark-800 shadow-sm rounded-lg border border-gray-200 dark:border-dark-700">
        <DataTable
          data={propertyTypes}
          columns={columns}
          loading={isLoading}
          actions={actions}
          bulkActions={bulkActions}
          searchable
          searchPlaceholder="Search property types..."
          onSearch={(value) => handleFilterChange("search", value)}
          pagination={{
            current: currentPage,
            pageSize,
            total: pagination?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: true,
            onChange: handlePageChange,
          }}
          sortable
          onSort={(sort) => {
            if (sort) {
              handleSort(sort.field);
            }
          }}
          rowKey="id"
          size="md"
          bordered={false}
          striped
          hoverable
        />
      </div>

      {/* Create and Edit are handled by dedicated pages */}
    </div>
  );
};

export default PropertyTypesPage;
